import type {
  EvidenceOrderPuzzle,
  GameContent,
  HotspotSequencePuzzle,
  LevelDefinition,
  PhotoCodePuzzle,
  PhotoSequencePuzzle,
  RewardDefinition,
  ScrollQuizPuzzle,
  ScrollHotspotPuzzle,
  SlidingPuzzle,
  SpotDifferencePuzzle,
  VerticalStripPuzzle,
} from './types';

const CONTENT_URL = './content/levels.json';

function fail(message: string): never {
  throw new Error(`内容配置错误：${message}`);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') fail(`${path} 必须是非空文字`);
  return value.trim();
}

function requireNumber(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${path} 必须是数字`);
  return value;
}

function requireInteger(value: unknown, path: string, minimum = 1): number {
  const number = requireNumber(value, path);
  if (!Number.isInteger(number) || number < minimum) fail(`${path} 必须是不小于 ${minimum} 的整数`);
  return number;
}

function requirePoint(value: unknown, path: string): asserts value is Record<string, unknown> & { x: number; y: number } {
  if (!isObject(value)) fail(`${path} 必须是坐标对象`);
  const x = requireNumber(value.x, `${path}.x`);
  const y = requireNumber(value.y, `${path}.y`);
  if (x < 0 || x > 1 || y < 0 || y > 1) fail(`${path} 坐标必须在 0 到 1 之间`);
}

function validateReward(value: unknown, path: string): asserts value is RewardDefinition {
  if (!isObject(value)) fail(`${path} 必须是奖励对象`);
  const mode = value.mode;
  if (mode !== 'image' && mode !== 'text' && mode !== 'mixed') fail(`${path}.mode 不受支持`);
  requireString(value.title, `${path}.title`);
  if (mode === 'image' || mode === 'mixed') {
    requireString(value.image, `${path}.image`);
    requireString(value.alt, `${path}.alt`);
  }
  if (mode === 'text' || mode === 'mixed') requireString(value.body, `${path}.body`);
}

function validateSpotPuzzle(value: unknown, path: string): asserts value is SpotDifferencePuzzle {
  if (!isObject(value)) fail(`${path} 必须是找不同配置`);
  requireString(value.image, `${path}.image`);
  if (!Array.isArray(value.differences) || value.differences.length < 1) fail(`${path}.differences 不能为空`);
  value.differences.forEach((item, index) => {
    const itemPath = `${path}.differences[${index}]`;
    requirePoint(item, itemPath);
    requireString(item.id, `${itemPath}.id`);
    requireString(item.label, `${itemPath}.label`);
    const radius = requireNumber(item.radius, `${itemPath}.radius`);
    if (radius <= 0 || radius > 0.25) fail(`${itemPath}.radius 超出合理范围`);
    if (!['glow', 'ring', 'slash', 'diamond', 'dots'].includes(String(item.mark))) fail(`${itemPath}.mark 不受支持`);
  });
}

function validateSlidingPuzzle(value: unknown, path: string): asserts value is SlidingPuzzle {
  if (!isObject(value)) fail(`${path} 必须是滑块拼图配置`);
  requireString(value.image, `${path}.image`);
  if (value.gridSize !== 3 && value.gridSize !== 4) fail(`${path}.gridSize 只能是 3 或 4`);
  if (requireNumber(value.scrambleMoves, `${path}.scrambleMoves`) < 4) fail(`${path}.scrambleMoves 太小`);
  requireNumber(value.seed, `${path}.seed`);
}

function validateHotspotPuzzle(value: unknown, path: string): asserts value is HotspotSequencePuzzle {
  if (!isObject(value)) fail(`${path} 必须是热点线索配置`);
  requireString(value.image, `${path}.image`);
  if (!Array.isArray(value.hotspots) || !Array.isArray(value.steps) || value.steps.length < 1) fail(`${path} 缺少热点或步骤`);
  const ids = new Set<string>();
  value.hotspots.forEach((item, index) => {
    const itemPath = `${path}.hotspots[${index}]`;
    requirePoint(item, itemPath);
    ids.add(requireString(item.id, `${itemPath}.id`));
    requireString(item.label, `${itemPath}.label`);
    requireNumber(item.radius, `${itemPath}.radius`);
  });
  value.steps.forEach((step, index) => {
    const stepPath = `${path}.steps[${index}]`;
    if (!isObject(step)) fail(`${stepPath} 必须是步骤对象`);
    requireString(step.prompt, `${stepPath}.prompt`);
    const hotspotId = requireString(step.hotspotId, `${stepPath}.hotspotId`);
    if (!ids.has(hotspotId)) fail(`${stepPath} 引用了不存在的热点`);
  });
}

function validateScrollHotspotPuzzle(value: unknown, path: string): asserts value is ScrollHotspotPuzzle {
  validateHotspotPuzzle(value, path);
}

function validatePermutation(value: unknown, count: number, path: string): asserts value is number[] {
  if (!Array.isArray(value) || value.length !== count) fail(`${path} 必须包含 ${count} 个位置`);
  const expected = new Set(Array.from({ length: count }, (_, index) => index));
  value.forEach((entry) => {
    if (!Number.isInteger(entry) || !expected.delete(Number(entry))) fail(`${path} 必须是从 0 开始且不重复的完整顺序`);
  });
}

function validateVerticalStripPuzzle(value: unknown, path: string): asserts value is VerticalStripPuzzle {
  if (!isObject(value)) fail(`${path} 必须是长卷碎条配置`);
  requireString(value.image, `${path}.image`);
  const stripCount = requireInteger(value.stripCount, `${path}.stripCount`, 3);
  if (stripCount > 6) fail(`${path}.stripCount 不能大于 6`);
  validatePermutation(value.initialOrder, stripCount, `${path}.initialOrder`);
}

function validatePhotoSequencePuzzle(value: unknown, path: string): asserts value is PhotoSequencePuzzle {
  if (!isObject(value)) fail(`${path} 必须是照片段落排序配置`);
  requireString(value.image, `${path}.image`);
  const panelCount = requireInteger(value.panelCount, `${path}.panelCount`, 3);
  if (panelCount > 6) fail(`${path}.panelCount 不能大于 6`);
  validatePermutation(value.initialOrder, panelCount, `${path}.initialOrder`);
}

function validateScrollQuizPuzzle(value: unknown, path: string): asserts value is ScrollQuizPuzzle {
  if (!isObject(value)) fail(`${path} 必须是长卷观察问答配置`);
  requireString(value.image, `${path}.image`);
  if (!Array.isArray(value.questions) || value.questions.length < 3 || value.questions.length > 6) {
    fail(`${path}.questions 必须包含 3 到 6 道题`);
  }
  value.questions.forEach((question, index) => {
    const questionPath = `${path}.questions[${index}]`;
    if (!isObject(question)) fail(`${questionPath} 必须是题目对象`);
    requireString(question.prompt, `${questionPath}.prompt`);
    requireString(question.hint, `${questionPath}.hint`);
    requireString(question.success, `${questionPath}.success`);
    const focusY = requireNumber(question.focusY, `${questionPath}.focusY`);
    if (focusY < 0 || focusY > 1) fail(`${questionPath}.focusY 必须在 0 到 1 之间`);
    if (!Array.isArray(question.options) || question.options.length !== 3) fail(`${questionPath}.options 必须恰好包含 3 项`);
    question.options.forEach((option, optionIndex) => requireString(option, `${questionPath}.options[${optionIndex}]`));
    const correctIndex = requireInteger(question.correctIndex, `${questionPath}.correctIndex`, 0);
    if (correctIndex >= question.options.length) fail(`${questionPath}.correctIndex 超出选项范围`);
  });
}

function validatePhotoCodePuzzle(value: unknown, path: string): asserts value is PhotoCodePuzzle {
  if (!isObject(value)) fail(`${path} 必须是长卷密码配置`);
  requireString(value.image, `${path}.image`);
  const code = requireString(value.code, `${path}.code`);
  if (!/^\d{4}$/.test(code)) fail(`${path}.code 必须是 4 位数字`);
  if (!Array.isArray(value.clues) || value.clues.length !== code.length) {
    fail(`${path}.clues 必须与 4 位密码一一对应`);
  }
  value.clues.forEach((clue, index) => {
    const cluePath = `${path}.clues[${index}]`;
    if (!isObject(clue)) fail(`${cluePath} 必须是线索对象`);
    requireString(clue.prompt, `${cluePath}.prompt`);
    requireString(clue.hint, `${cluePath}.hint`);
    const focusY = requireNumber(clue.focusY, `${cluePath}.focusY`);
    if (focusY < 0 || focusY > 1) fail(`${cluePath}.focusY 必须在 0 到 1 之间`);
  });
}

function validateEvidencePuzzle(value: unknown, path: string): asserts value is EvidenceOrderPuzzle {
  if (!isObject(value)) fail(`${path} 必须是证据排序配置`);
  requireString(value.image, `${path}.image`);
  requireString(value.clue, `${path}.clue`);
  if (!Array.isArray(value.items) || value.items.length !== 4) fail(`${path}.items 必须恰好包含 4 项`);
  const ids = new Set<string>();
  value.items.forEach((item, index) => {
    const itemPath = `${path}.items[${index}]`;
    if (!isObject(item) || !isObject(item.crop)) fail(`${itemPath} 格式错误`);
    ids.add(requireString(item.id, `${itemPath}.id`));
    requireString(item.label, `${itemPath}.label`);
    requirePoint(item.crop, `${itemPath}.crop`);
    const width = requireNumber(item.crop.width, `${itemPath}.crop.width`);
    const height = requireNumber(item.crop.height, `${itemPath}.crop.height`);
    if (width <= 0 || height <= 0 || Number(item.crop.x) + width > 1 || Number(item.crop.y) + height > 1) {
      fail(`${itemPath}.crop 超出图片范围`);
    }
  });
  for (const field of ['initialOrder', 'correctOrder'] as const) {
    const order = value[field];
    if (!Array.isArray(order) || order.length !== ids.size || order.some((id) => typeof id !== 'string' || !ids.has(id))) {
      fail(`${path}.${field} 必须完整引用全部证据`);
    }
  }
}

export function validateContent(value: unknown): GameContent {
  if (!isObject(value) || value.version !== 1 || !isObject(value.game) || !Array.isArray(value.levels)) {
    fail('根节点必须包含 version、game 和 levels');
  }
  requireString(value.game.title, 'game.title');
  requireString(value.game.eyebrow, 'game.eyebrow');
  requireString(value.game.tagline, 'game.tagline');
  requireString(value.game.opening, 'game.opening');
  if (value.levels.length !== 6) fail('首版必须包含 6 个关卡');

  const ids = new Set<string>();
  value.levels.forEach((raw, index) => {
    const path = `levels[${index}]`;
    if (!isObject(raw)) fail(`${path} 必须是关卡对象`);
    const id = requireString(raw.id, `${path}.id`);
    if (ids.has(id)) fail(`${path}.id 不能重复`);
    ids.add(id);
    if (raw.order !== index + 1) fail(`${path}.order 必须从 1 连续递增`);
    requireString(raw.dossier, `${path}.dossier`);
    requireString(raw.title, `${path}.title`);
    requireString(raw.subtitle, `${path}.subtitle`);
    requireString(raw.instructions, `${path}.instructions`);
    validateReward(raw.reward, `${path}.reward`);
    switch (raw.kind) {
      case 'spot-difference':
        validateSpotPuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'sliding-puzzle':
        validateSlidingPuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'hotspot-sequence':
        validateHotspotPuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'evidence-order':
        validateEvidencePuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'scroll-hotspot':
        validateScrollHotspotPuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'vertical-strip':
        validateVerticalStripPuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'photo-sequence':
        validatePhotoSequencePuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'scroll-quiz':
        validateScrollQuizPuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      case 'photo-code':
        validatePhotoCodePuzzle(raw.puzzle, `${path}.puzzle`);
        break;
      default:
        fail(`${path}.kind 不受支持`);
    }
  });

  const finalPuzzle = value.finalPuzzle;
  if (!isObject(finalPuzzle)) fail('finalPuzzle 必须是终章拼图配置');
  requireString(finalPuzzle.title, 'finalPuzzle.title');
  requireString(finalPuzzle.caption, 'finalPuzzle.caption');
  requireString(finalPuzzle.fullImage, 'finalPuzzle.fullImage');
  requireString(finalPuzzle.alt, 'finalPuzzle.alt');
  const columns = requireInteger(finalPuzzle.columns, 'finalPuzzle.columns');
  const rows = requireInteger(finalPuzzle.rows, 'finalPuzzle.rows');
  if (!Array.isArray(finalPuzzle.pieces) || finalPuzzle.pieces.length !== value.levels.length || columns * rows !== finalPuzzle.pieces.length) {
    fail('finalPuzzle.pieces 必须与六关及拼图格数一一对应');
  }
  const pieceLevels = new Set<string>();
  const piecePositions = new Set<number>();
  finalPuzzle.pieces.forEach((piece, index) => {
    const path = `finalPuzzle.pieces[${index}]`;
    if (!isObject(piece)) fail(`${path} 必须是拼图块配置`);
    const levelId = requireString(piece.levelId, `${path}.levelId`);
    if (!ids.has(levelId) || pieceLevels.has(levelId)) fail(`${path}.levelId 必须唯一对应一个关卡`);
    pieceLevels.add(levelId);
    requireString(piece.image, `${path}.image`);
    requireString(piece.alt, `${path}.alt`);
    const position = requireInteger(piece.position, `${path}.position`, 0);
    if (position >= columns * rows || piecePositions.has(position)) fail(`${path}.position 必须是唯一且有效的格位`);
    piecePositions.add(position);
  });
  return value as unknown as GameContent;
}

export async function loadContent(): Promise<GameContent> {
  const response = await fetch(CONTENT_URL, { cache: 'no-store' });
  if (!response.ok) throw new Error(`无法读取关卡配置（${response.status}）`);
  return validateContent(await response.json());
}

export function getLevelById(content: GameContent, id: string): LevelDefinition | undefined {
  return content.levels.find((level) => level.id === id);
}
